import BarChart from '../charts/barChart';
import ChartCards from './chartCards';
import CustomerApi from 'api/customer';
import DateRangeSelector from './dateRangeSelector';
import dealerApi from 'api/dealer';
import downloadCSV from 'helpers/csvHelpers';
import events from 'lib/events';
import InformationCard from './informationCard';
import LayoutDatePicker from 'components/forms/layoutDatePicker';
import moment from 'moment';
import ProductProfile from './productProfile';
import PropTypes from 'prop-types';
import React from 'react';
import session from 'services/sessionService';
import ToolTip, {tooltipPositions} from '../ToolTip';
import {dateTimeFormats} from 'helpers/constants';
import {formatCurrency} from 'helpers/currencyHelper';
import {FormattedMessage, intlShape, injectIntl} from 'react-intl';
import cloneDeep from 'lodash/cloneDeep';

let SeasonMonths;

const dateCodes = {
  all: 'ALL',
  custom: 'CUSTOM',
  lastThreeYears: 'LTY',
  lastYear: 'LY',
  monthToDate: 'MTD',
  weekToDate: 'WTD',
  yearToDate: 'YTD',
};

const today = moment().endOf('day');

const purchaseSummaryDates = [
  {title: 'general.weektodate', code: dateCodes.weekToDate, from: moment().startOf('week'), to: today},
  {title: 'general.monthtoday', code: dateCodes.monthToDate, from: moment().startOf('month'), to: today},
  {title: 'general.yeartodate', code: dateCodes.yearToDate, from: moment().startOf('year'), to: today},
];

const recommendedVsPurchasedDates = [
  {title: 'general.yeartodate', code: dateCodes.yearToDate},
  {title: 'general.last.year', code: dateCodes.lastYear},
  {title: 'general.last.3years', code: dateCodes.lastThreeYears},
  {title: 'general.all', code: dateCodes.all},
];

class PurchaseSummary extends React.Component {
  static propTypes = {
    contactName: PropTypes.string,
    intl: intlShape.isRequired,
    id: PropTypes.number,
  };

  constructor(props) {
    super(props);
    this.state = {
      avgPurchasePerAnalysis: 0,
      data: null,
      dealer: null,
      filterDateFrom: moment(),
      filterDateTo: moment(),
      lastCode: null,
      lifeTimePurchases: 0,
      maintanenceProductsCards: [],
      otherProductsCards: [],
      periodPurchases: 0,
      periodWasterTests: 0,
      productProfiles: [],
      recommendedSortedLabel: this.props.intl.formatMessage({id: 'general.date.range'}, {
        start: moment().startOf('year').format(dateTimeFormats.numeralDate),
        end: moment().format(dateTimeFormats.numeralDate),
      }),
      showCustomDate: false,
      threeStepPurchase: 0,
      totalPurchasesChartData: null,
    };
  }

  componentDidMount() {
    dealerApi.getDealer(parseInt(session.getUser().dealerId))
      .then(res => {
        this.setState({
          dealer: res,
        });
        this.updatePurchaseSummary(dateCodes.yearToDate);
        this.updateRecommendedCards();
      });
  }

  getFormattedNumber = (number) => {
    if (!number) {
      return 0;
    }
    return number % 1 !== 0 ? number.toFixed(2) : number;
  };

  getMonthsArray = (from, to) => {
    const timeValues = [];

    while (to > from) {
      timeValues.push(from.format('MMM').toLowerCase());
      from.add(1, 'month');
    }
    return timeValues;
  };

  setLabelsColors = (data) => {
    const newArray = [];

    SeasonMonths = SeasonMonths || this.getMonthsArray(this.state.dealer.seasonOpen, this.state.dealer.seasonClose);
    data.forEach((element) => {
      if (element.name && SeasonMonths.indexOf(element.name.toLowerCase()) >= 0) {
        element.labelColor = '#0088cc';
      } else {
        element.labelColor = '#000';
      }
      newArray.push(element);
    }, this);
    return newArray;
  };

  changeDateFrom = (data) => {
    this.setState({
      filterDateFrom: data.value,
    }, this.customDatesDone);
  };

  changeDateTo = (data) => {
    this.setState({
      filterDateTo: data.value,
    }, this.customDatesDone);
  };

  customDatesDone = () => {
    if (this.state.filterDateTo < this.state.filterDateFrom) {
      events.publish('error.user', {
        message: this.props.intl.formatMessage({id: 'general.error.invalid.dates'}),
        key: 'invalidDates',
      });
    } else {
      this.updatePurchaseSummary(dateCodes.custom, this.state.filterDateFrom, this.state.filterDateTo);
    }
  };

  editKeys = (data, category, value) => {
    const newArray = [];

    data.forEach((obj) => {
      const lowerObj = Object.keys(obj).reduce((acc, x) => {
        if (x === category) {
          acc['name'] = obj[x];
        } else if (x === value) {
          acc['value'] = obj[x];
        } else {
          acc[x] = obj[x];
        }
        return acc;
      }, {});

      newArray.push(lowerObj);
    }, this);
    return newArray;
  };

  exportReport = () => {
    const {maintanenceProductsCards, otherProductsCards} = this.state;
    let finalData = maintanenceProductsCards.concat(otherProductsCards);
    let {formatMessage} = this.props.intl;

    if (finalData && finalData.length > 1) {
      const fields = ['ProductName', 'Source', 'Purchased', 'Recommended', 'Unit', 'LastPurchaseDate'];
      const headings = [
        formatMessage({id: 'general.product.name'}),
        formatMessage({id: 'general.source'}),
        formatMessage({id: 'general.purchased'}),
        formatMessage({id: 'general.recommended'}),
        formatMessage({id: 'general.unit'}),
        formatMessage({id: 'general.last.purchased.date'}),
      ];

      downloadCSV(finalData, fields, headings,
        // TODO: better i18n here
        this.props.contactName + ' ' + formatMessage({id: 'customer.products.recommended.purchased'}) +
        ' ' + this.state.recommendedSortedLabel + '.csv');
    }
  };

  manageCustomDate = (value) => {
    this.setState({
      showCustomDate: value,
    });
  };

  updatePurchaseSummary = (code, from, to) => {
    if (!code) {
      code = dateCodes.custom;
    }

    CustomerApi.getCustomerPurchaseSummary(this.props.id,
      code,
      from ? from.format(dateTimeFormats.numeralDate) : null,
      to ? to.format(dateTimeFormats.numeralDate) : null)
      .then(res => {
        let data = this.editKeys(res.Purchases, 'Label', 'Purchases');

        if (code === dateCodes.yearToDate) {
          data = this.setLabelsColors(data);
        }

        let nextState = {
          avgPurchasePerAnalysis: this.getFormattedNumber(res.AvgPurchasePerAnalysis),
          lastCode: code,
          lifeTimePurchases: this.getFormattedNumber(res.LifeTimePurchases),
          periodPurchases: this.getFormattedNumber(res.PeriodPurchases),
          periodWasterTests: res.PeriodWasterTests,
          threeStepPurchase: this.getFormattedNumber(res.ThreeStepPurchase),
          totalPurchasesChartData: data,
        };

        let matchedCode = purchaseSummaryDates.find(x => x.code === code);

        if (matchedCode) {
          nextState.filterDateFrom = matchedCode.from;
          nextState.filterDateTo = matchedCode.to;
        }

        this.setState(nextState);
      });
  };

  updateRecommendedCards = (code) => {
    if (!code) {
      code = dateCodes.yearToDate;
    }

    CustomerApi.getCustomerPurchaseSalesHistory(this.props.id, code)
      .then(res => {

        let {formatMessage} = this.props.intl;

        this.setState({
          maintanenceProductsCards: res.MaintanenceProducts,
          otherProductsCards: res.OtherProducts,
          productProfiles: res.ProductProfiles,
        });

        if (code === dateCodes.all) {
          let allTimeLabel = formatMessage({id: 'general.all.time'});

          this.setState({
            recommendedSortedLabel: allTimeLabel,
          });
        } else if (code === dateCodes.yearToDate) {
          let yearToDateLabel = formatMessage({id: 'general.date.range'}, {
            start: moment().startOf('year').format(dateTimeFormats.numeralDate),
            end: moment().format(dateTimeFormats.numeralDate),
          });

          this.setState({
            recommendedSortedLabel: yearToDateLabel,
          });
        } else if (code === dateCodes.lastYear) {
          let lastYearLabel = formatMessage({id: 'general.date.range'}, {
            start: moment().startOf('year').add(-1, 'years').format(dateTimeFormats.numeralDate),
            end: moment().endOf('year').add(-1, 'years').format(dateTimeFormats.numeralDate),
          });

          this.setState({
            recommendedSortedLabel: lastYearLabel,
          });
        }
        if (code === dateCodes.lastThreeYears) {
          let lastThreeYearsLabel = formatMessage({id: 'general.date.range'}, {
            start: moment().add(-3, 'years').format(dateTimeFormats.numeralDate),
            end: moment().format(dateTimeFormats.numeralDate),
          });

          this.setState({
            recommendedSortedLabel: lastThreeYearsLabel,
          });
        }
      });
  };

  render() {
    let otherProductsCards;
    let PurchaseSummaryChart;
    let maintanenceProductsCards;
    let productProfiles;
    let allowExport = false;
    let {formatMessage, locale} = this.props.intl;

    if (this.state.totalPurchasesChartData && this.state.totalPurchasesChartData.length > 0) {
      const chartData = this.state.totalPurchasesChartData
        .map(x => {
          x = cloneDeep(x);
          if (this.state.lastCode === dateCodes.yearToDate) {
            x.name = moment().month(x.name).format('MMM');
          } else if (this.state.lastCode === dateCodes.weekToDate) {
            moment.locale('en-US');
            x.name = moment().day(x.name).format();
            moment.locale(locale);
            x.name = moment(x.name).format('dddd');
          }
          return x;
        });

      PurchaseSummaryChart = <BarChart data={chartData}
                                       verticalAxisTitle={formatMessage({id: 'general.total.purchases'}) + ' ($)'}
                                       colors={['#87c437']}
                                       labelRotation={0}
                                       balloonText={formatMessage({id: 'general.total.purchases'}) + `: <b>$[[value]]</b>
          <br>` + formatMessage({id: 'general.total.water.tests'}) + `: <b>[[WaterTests]]</b>
          <br>${formatMessage({id: this.state.lastCode === dateCodes.yearToDate ? 'general.month' : 'general.day.week'})}: <b>[[name]]</b>`}
                                       gridThickness={0}
                                       customLabelColor={true}
      />;
    }

    if (this.state.maintanenceProductsCards && this.state.maintanenceProductsCards.length > 0) {
      maintanenceProductsCards = <ChartCards cards={this.state.maintanenceProductsCards}/>;
      allowExport = true;
    }

    if (this.state.otherProductsCards && this.state.otherProductsCards.length > 0) {
      otherProductsCards = <ChartCards cards={this.state.otherProductsCards}/>;
      allowExport = true;
    }

    if (this.state.productProfiles && this.state.productProfiles.length > 0) {
      productProfiles = <ProductProfile products={this.state.productProfiles}
                                        customerID={this.props.id}/>;
    }

    return <div>
      <h2><FormattedMessage id="customer.purchase.summary.title"/></h2>
      <div className="purchase-summary-container">
        <div className="date-range-container container-fluid">
          <div className="row">
            <div className={this.state.showCustomDate ? 'hide-component' : 'col-sm-5'}
                 onClick={this.manageCustomDate.bind(this, false)}>
              <FormattedMessage id="general.date.range"
                                values={{
                                  start: this.state.filterDateFrom.format('ll'),
                                  end: this.state.filterDateTo.format('ll'),
                                }}/>
              <br/>
              <DateRangeSelector
                dates={purchaseSummaryDates}
                selected={2}
                onSelectedItem={this.updatePurchaseSummary}
              />
            </div>
            <div className={this.state.showCustomDate ? 'col-sm-2 custom-date-field' : 'hide-component'}>
              <LayoutDatePicker label={formatMessage({id: 'general.date.from'})}
                                value={this.state.filterDateFrom}
                                editMode={true}
                                dateFormat={'MMM. DD, YYYY'}
                                dataKey='filterPublishSummaryFrom'
                                onChange={this.changeDateFrom}/>
            </div>
            <div className={this.state.showCustomDate ? 'col-sm-2 custom-date-field' : 'hide-component'}>
              <LayoutDatePicker label={formatMessage({id: 'general.date.to'})}
                                value={this.state.filterDateTo}
                                editMode={true}
                                dateFormat={'MMM. DD, YYYY'}
                                dataKey='filterPublishSummaryTo'
                                onChange={this.changeDateTo}/>
            </div>
            <div className="col-sm-2 custom-date">
              <a
                onClick={this.manageCustomDate.bind(this, !this.state.showCustomDate)}>
                {this.state.showCustomDate ? formatMessage({id: 'general.date.range.title'}) : formatMessage({id: 'general.custom.date'})}
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="purchase-summary-data">
        <InformationCard title={formatMessage({id: 'customer.water.analyses'})}
                         data={this.state.periodWasterTests ? this.state.periodWasterTests : 0}/>
        <InformationCard title={formatMessage({id: 'general.total.bioguard.purchases'})}
                         data={this.state.periodPurchases ? formatCurrency(this.state.periodPurchases) : 0}/>
        <InformationCard title={formatMessage({id: 'customer.spend.water.analysis'})}
                         data={this.state.avgPurchasePerAnalysis ? formatCurrency(this.state.avgPurchasePerAnalysis) : 0}/>
        <InformationCard title={formatMessage({id: 'customer.3step.program'})}
                         data={this.state.threeStepPurchase ? formatCurrency(this.state.threeStepPurchase) : 0}/>
        <InformationCard title={formatMessage({id: 'customer.purchase.lifetime.purchases'})}
                         data={this.state.lifeTimePurchases ? formatCurrency(this.state.lifeTimePurchases) : 0}/>
      </div>
      <div className="purchase-summary-chart">
        {PurchaseSummaryChart}
      </div>
      <div className="recommended-vs-purchased">
        <div className="rvp-title">
          <h2><FormattedMessage id="customer.recommended.purchased.title"/> <ToolTip
            title='Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec tincidunt arcu id imperdiet molestie.'
            position={tooltipPositions.topRight}
            iconOnly/></h2>
          {allowExport ?
            <a onClick={this.exportReport}><FormattedMessage id="general.export"/>
              <i className='bl bl-right_arrow'/></a> :
            <div/>}
        </div>
        <div className="rvp-sort container-fluid">
          <div className="row">
            <div className="col-sm-8">
              <FormattedMessage id="general.sortby"/>
              <br/>
              <DateRangeSelector
                dates={recommendedVsPurchasedDates}
                selected={0}
                onSelectedItem={this.updateRecommendedCards}
              />
            </div>
            <div className="col-sm-4">
              <span className="data-from"><FormattedMessage
                id="customer.data.displayed.from"/> {this.state.recommendedSortedLabel}</span>
            </div>
          </div>
        </div>
        <div className="recommended-statics">
          <h2 className="border-bottom"><FormattedMessage id="customer.maintenance.products"/></h2>
          {maintanenceProductsCards}
          <h2 className="border-bottom other-products-header"><FormattedMessage id="customer.other.products"/></h2>
          {otherProductsCards}
          {productProfiles}
        </div>
      </div>
    </div>;
  }
}

export default injectIntl(PurchaseSummary);
